import {
  IStyles,
  IconWrapper,
  InputField,
  Loader,
  PrimaryButton,
  SecondaryButton,
  deserify,
  getLocalStorage,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
  theme,
} from '@geneo2-web/shared-ui';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import {
  Box,
  ButtonBase,
  FormControlLabel,
  InputBase,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AssignmentConfigEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  combineStartTimeAndDate,
  convertTimeStringToTimestamp,
} from '../../../utils/functions';

import {
  ASSESSMENT_CONGRATULATIONS,
  Project_Congratulations,
  Project_Dashboard,
} from '../../../routeHandling/RoutesNomenclature';
import { Timestamp } from '@bufbuild/protobuf';

import { SectionStudentInfo } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { useGlobalContext } from 'apps/teacher/src/app/Context/GlobalContextProvider';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { useDownloadContext } from 'apps/teacher/src/app/Context/DownloadContextProviderV2';
import { AddGroupPopup } from '../components/AddGroupPopup';
import { PreviousGroupsPanel } from '../components/PreviousGroupsPanel';
import {
  useCurrentMinute,
  wrongEndTime,
  wrongStartTime,
} from '../../Assessment/utils/assessment.utils';
import { StartAndEndPointPicker } from '../../Assessment/Common/AssignAssessment/components/StartAndEndPointPicker';
import { HomeworkInstructionsPopup } from '../../Assessment/Common/AssignAssessment/components/HomeworkInstructions';
import { interactionEvent } from '../../Auth/auth.events';
import {
  DEFAULT_PROJECT_MARKS,
  IProjectStudentGroup,
  MAX_PROJECT_NAME_LENGTH,
  PROJECT_MARKS_STEP,
  setAssignedStudentGroups,
  setProjectInstructions,
  setProjectMarks,
  setProjectName,
} from '../CreateProject/reducer/projectFlow.Slice';
import {
  createClientGroupId,
  getStudentIdsTakenByOtherGroups,
  getUngroupedStudents,
  mergeGroupResults,
  toCollaborativeGroupInputs,
  validateProjectGroups,
} from './projectGroups.utils';

const styles: IStyles = {
  root: {
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: { xs: pxToRem(20), lg: pxTovW(241) },
    paddingRight: { xs: pxToRem(20), lg: pxTovW(241) },
    paddingTop: { xs: pxToRem(20), lg: pxTovW(40) },
    paddingBottom: { xs: pxToRem(20), lg: pxTovW(40) },
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: { xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) },
  },
  inputFeildBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) },
    width: { xs: '100%', lg: pxTovW(856) },
    mb: '2.343vw',
  },
  instructionBox: {
    width: { xs: '100%', md: '100%' },
    boxSizing: 'border-box',
    marginTop: { xs: '30px', sm: '30px', lg: 0 },
  },
  instructionCard: {
    display: 'flex',
    flexDirection: 'column',
    padding: { xs: pxToRem(14), lg: pxTovW(10) },
    boxSizing: 'border-box',
    paddingBottom: { xs: pxToRem(14), md: pxToRem(35), lg: pxTovW(47) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(15), lg: pxTovW(15) },
    width: { xs: '100%' },
    backgroundColor: '#F6FBFF',
    border: '1px solid #CCE6FE',
    gap: { xs: pxToRem(20), md: pxToRem(16), lg: pxTovW(16) },
    marginBottom: { xs: pxToRem(60) },
  },
  textBox: {
    display: 'flex',
    width: '100%',
    paddingLeft: { md: pxToRem(10), lg: pxTovW(10) },
    paddingRight: { md: pxToRem(10), lg: pxTovW(10) },
    gap: { xs: pxToRem(6.61), md: pxToRem(6.61), lg: pxTovW(6.61) },
    alignItems: 'flex-start',
  },
  inputField: {
    height: { xs: pxToRem(50), md: pxToRem(48), lg: pxTovW(70) },
    borderRadius: { xs: pxToRem(10), md: pxToRem(15), lg: pxTovW(15) },
    border: '1px solid #CCE6FE',
    width: { xs: '100%', lg: pxTovW(856) },
    justifyContent: 'center',
  },
  inputBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
  },
  labelTypo: {
    fontSize: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(18) },
  },
  groupRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxToRem(16), lg: pxTovW(16) },
    padding: { xs: pxToRem(10), md: pxToRem(12), lg: pxTovW(12) },
    border: '1px solid #CCE6FE',
    borderRadius: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
    mb: { xs: pxToRem(10), md: pxToRem(10), lg: pxTovW(10) },
  },
  marksBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: { xs: pxToRem(140), md: pxToRem(108), lg: pxTovW(170) },
    height: { xs: pxToRem(50), md: pxToRem(45), lg: pxTovW(64) },
    border: '2px solid #34A853',
    borderRadius: { xs: pxToRem(12), md: pxToRem(15), lg: pxTovW(15) },
    padding: { xs: `0 ${pxToRem(16)}`, md: `0 ${pxToRem(18)}`, lg: `0 ${pxTovW(20)}` },
    boxSizing: 'border-box',
  },
  marksSymbol: {
    fontSize: { xs: pxToRem(24), md: pxToRem(22), lg: pxTovW(26) },
    fontWeight: 700,
    color: '#34A853',
    cursor: 'pointer',
    userSelect: 'none',
    lineHeight: 1,
  },
  marksValue: {
    flex: 1,
    minWidth: 0,
    fontSize: { xs: pxToRem(20), md: pxToRem(20), lg: pxTovW(22) },
    fontWeight: 700,
    color: '#34A853',
    '& input': { textAlign: 'center', padding: 0 },
  },
};

// The backend rejects project max marks below this.
const MIN_PROJECT_MARKS = 1;

interface IDataList {
  name: string;
  id: bigint;
  selected: boolean;
}

export const AssignProject = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const { isOffline } = useDownloadContext();
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const minute = useCurrentMinute();
  const { setSelectedFunction } = useGlobalContext();

  const {
    created_assessment_id,
    project_name,
    project_instructions,
    project_marks,
    selected_class,
    selected_section,
    selected_subject,
  } = deserify(useAppSelector((state) => state.projectFlow));

  const {
    LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler,
  } = useCommonServiceClientContext();

  const [studentListPopupOpen, setStudentListPopupOpen] = useState(false);
  const [previousGroupsPanelOpen, setPreviousGroupsPanelOpen] = useState(false);
  const [openInstructionsPopup, setOpenInstructionsPopup] = useState(false);
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const [groups, setGroups] = useState<IProjectStudentGroup[]>([]);
  const [editingGroup, setEditingGroup] = useState<IProjectStudentGroup | null>(
    null
  );
  const [assignedStudents, setAssignedStudents] = useState<
    SectionStudentInfo[]
  >([]);
  const [taskName, setTaskName] = useState(project_name);
  // Kept as a string so the teacher can clear the field while typing.
  const [marks, setMarks] = useState(
    String(project_marks ?? DEFAULT_PROJECT_MARKS)
  );
  const [allowLateSubmission, setAllowLateSubmission] = useState(false);

  const decrementMarks = () =>
    setMarks((prev) =>
      String(Math.max(MIN_PROJECT_MARKS, Number(prev) - PROJECT_MARKS_STEP))
    );
  const incrementMarks = () =>
    setMarks((prev) => String(Number(prev) + PROJECT_MARKS_STEP));
  const handleMarksChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (/^\d*$/.test(event.target.value)) setMarks(event.target.value);
  };

  // A project is created for one section, so that is the section it assigns to.
  const [sectionList, setSectionList] = useState<IDataList[]>(
    user_info?.teachClassSubjects
      ?.filter((elem) => selected_class?.id === elem.classId)
      .map((elem) => ({
        id: elem.sectionId,
        name: elem.sectionName,
        selected: elem.sectionId === selected_section?.id,
      })) || []
  );

  const [startTimeValues, setStartTimeValues] = useState({
    timeValue: `${new Date().getHours()} : ${new Date().getMinutes()}`,
    dateValue: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString(),
  });
  const [endTimeValues, setEndTimeValues] = useState({
    timeValue: '23:59',
    dateValue: new Date(
      Date.now() - new Date().getTimezoneOffset() * 60000
    ).toISOString(),
  });

  const teacher_id = deserify(getLocalStorage('userId'));

  const selectedSectionIds = useMemo(
    () => sectionList.filter((val) => val.selected).map((val) => val.id),
    [sectionList]
  );

  const ungroupedStudents = useMemo(
    () => getUngroupedStudents(groups, assignedStudents),
    [groups, assignedStudents]
  );

  // ------------------------------------------------------------

  const backButtonClick = async () => {
    navigate(-1);
  };

  useEffect(() => {
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  /**
   * The students this assignment covers. Groups must account for every one of
   * them, so the count is needed here and not only inside the group popup.
   */
  const fetchAssignedStudents = async () => {
    if (!teacher_id || !selected_class?.id || !selected_subject?.id) return;
    if (selectedSectionIds.length === 0) {
      setAssignedStudents([]);
      return;
    }
    try {
      const response =
        await LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.fetchSectionStudents(
          {
            personId: BigInt(teacher_id),
            classId: selected_class.id,
            // This request still takes int32 section ids.
            sectionIds: selectedSectionIds.map(Number),
            subjectId: selected_subject.id,
          }
        );
      setAssignedStudents(
        (response.sectionStudentsInfo || []).filter((student) =>
          selectedSectionIds.includes(student.sectionId)
        )
      );
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchAssignedStudents();
  }, [selectedSectionIds.join(','), selected_class?.id, selected_subject?.id]);

  // Dropping a section invalidates any group built from its students.
  useEffect(() => {
    setGroups((current) =>
      current.filter((group) => selectedSectionIds.includes(group.sectionId))
    );
  }, [selectedSectionIds.join(',')]);

  // ------------------------------------------------------------

  const deleteGroup = (clientGroupId: string) => {
    setGroups((current) =>
      current.filter((group) => group.clientGroupId !== clientGroupId)
    );
  };

  const assignClickHandler = () => {
    if (!created_assessment_id) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'No project to assign. Please create the project again.',
          open: true,
        })
      );
      return;
    }

    if (
      startTimeValues.timeValue === '' ||
      startTimeValues.dateValue === '' ||
      endTimeValues.dateValue === '' ||
      endTimeValues.timeValue === ''
    ) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Please select all Date and Time',
          open: true,
        })
      );
      return;
    }

    const currentTimeIsoString = new Date().toISOString();
    const startTimeIsoString =
      currentTimeIsoString > combineStartTimeAndDate(startTimeValues)
        ? currentTimeIsoString
        : combineStartTimeAndDate(startTimeValues);
    const startTime = new Date(startTimeIsoString);
    const endTime = new Date(combineStartTimeAndDate(endTimeValues));

    if (endTime < new Date(Date.now())) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Assessment deadline has passed.',
          open: true,
        })
      );
      return;
    }
    if (startTime > endTime) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Start date cannot be after End date',
          open: true,
        })
      );
      return;
    }
    if (startTime.toString() === endTime.toString()) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Start date and time cannot be same as End date and time',
          open: true,
        })
      );
      return;
    }
    if (taskName.trim() === '') {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: 'Project Name cannot be empty',
          open: true,
        })
      );
      return;
    }
    // The input caps typing, but the name is prefilled from the store and the
    // backend does not enforce a length, so this is the last line of defence.
    if (taskName.trim().length > MAX_PROJECT_NAME_LENGTH) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: `Project Name cannot be longer than ${MAX_PROJECT_NAME_LENGTH} characters`,
          open: true,
        })
      );
      return;
    }
    // The upper limit is a per-school setting only the backend knows, so
    // that bound is left to the server's error message.
    if (Number(marks) < MIN_PROJECT_MARKS) {
      dispatch(
        setToastInfo({
          variant: 'error',
          label: `Marks must be at least ${MIN_PROJECT_MARKS}`,
          open: true,
        })
      );
      return;
    }

    // Mirror the server's per-group rules so the teacher is not rejected at
    // the very end of the form. Groups are optional, and students left out
    // of every group simply do not get the project.
    const groupError = validateProjectGroups({
      groups,
      sectionIds: selectedSectionIds,
    });
    if (groupError) {
      dispatch(
        setToastInfo({ variant: 'error', label: groupError, open: true })
      );
      return;
    }

    assignmentSubmission({ startTime, endTime });
  };

  // ------------------------------------------------------------

  interface ISubmissionProps {
    startTime: Date;
    endTime: Date;
  }
  const assignmentSubmission = async (props: ISubmissionProps) => {
    const { endTime, startTime } = props;
    try {
      setLoading('loading');

      const response =
        await LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.assessmentAssignmentSubmit(
          {
            teacherId: teacher_id,
            assessmentId: created_assessment_id,
            assessmentName: taskName.trim(),
            startTime: Timestamp.fromDate(startTime),
            endTime: Timestamp.fromDate(endTime),
            dailyReminderTime: convertTimeStringToTimestamp('18:00'),
            sectionIds: selectedSectionIds,
            modeOfConduction: ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP,
            assessmentInstructions: project_instructions,
            assignmentInfo: [
              {
                configName:
                  AssignmentConfigEnum.ASSIGNMENT_CONFIG_ALLOW_LATE_SUBMISSION,
                isEnabled: allowLateSubmission,
              },
            ],
            // Omitted entirely when the teacher built no groups - that is
            // the ordinary one-submission-per-student assignment.
            studentGroups: groups.length
              ? toCollaborativeGroupInputs(groups)
              : undefined,
            // Total marks the project is graded out of.
            maxMarks: Number(marks),
          }
        );

      if (!response?.data) {
        throw new Error(response?.message || 'Assign project failed');
      }

      // Absent from the response whenever no groups were sent.
      dispatch(
        setAssignedStudentGroups(
          mergeGroupResults(groups, response.data.studentGroups ?? [])
        )
      );
      dispatch(setProjectName(taskName.trim()));
      dispatch(setProjectMarks(Number(marks)));
      setLoading('completed');

      await interactionEvent({
        url: 'Teacher_project_assign',
        context: 'assign_project',
        name: 'ASSIGN',
        pathSegments: pathSegments,
        isOffline: isOffline,
      });

      navigate(
        `${Project_Congratulations}/${response.data.updatedAssessment?.assessmentId ?? created_assessment_id
        }`
      );
    } catch (err: any) {
      setLoading('error');
      // failed_precondition means nothing was written - the teacher can fix
      // the groups and submit again safely.
      dispatch(
        setToastInfo({
          label: err?.message
            ? err.message.replace(/.*\]\s*/, '').trim()
            : 'Assign project failed',
          variant: 'error',
          open: true,
        })
      );
      console.log(err);
    }
  };

  // ------------------------------------------------------------

  const assignDisabled =
    wrongStartTime({ startTimeValues }).wrongDate ||
    wrongStartTime({ startTimeValues }).wrongTime ||
    wrongEndTime({ startTimeValues, endTimeValues }).wrongDate ||
    wrongEndTime({ startTimeValues, endTimeValues }).wrongTime ||
    selectedSectionIds.length === 0 ||
    // At least one group is required to assign the project.
    groups.length === 0;

  if (!created_assessment_id) {
    return (
      <Box sx={styles.root}>
        <Typography variant="h2">
          No project to assign. Please create a project first.
        </Typography>
        <PrimaryButton
          sx={{ mt: pxToRem(20), width: 'max-content' }}
          onClick={() => navigate(Project_Dashboard)}
        >
          <Typography variant="h4" color="common.white" fontWeight="bold">
            GO BACK
          </Typography>
        </PrimaryButton>
      </Box>
    );
  }

  return loading === 'loading' ? (
    <Loader />
  ) : (
    <Box sx={styles.root}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={styles.header}>
          <Typography variant="h1" fontWeight={600}>Assign Project</Typography>
          <Typography
            variant="cardText"
            sx={{ color: '#007CDC', fontWeight: 'normal', marginTop: '10px', fontSize: { xs: pxToRem(18), md: pxToRem(19), lg: pxTovW(21) } }}
          >
            {selected_class?.name || ''}
            {sectionList.find((s) => s.selected)?.name || ''} |{' '}
            {selected_subject?.name || ''}
          </Typography>
        </Box>
        <PrimaryButton
          key={`assign_${minute}`}
          disabled={assignDisabled}
          onClick={assignClickHandler}
          sx={{ display: { xs: 'none', md: 'unset' } }}
        >
          ASSIGN
        </PrimaryButton>
      </Box>

      <Box
        sx={{
          boxSizing: 'border-box',
          // Form and instructions sit side by side only on desktop; a tablet
          // is too narrow for the date pickers beside the instructions card.
          display: { lg: 'flex' },
          gap: { xs: pxToRem(10), md: pxToRem(28), lg: pxTovW(36) },
        }}
      >
        <Box sx={styles.inputFeildBox}>
          <Box sx={styles.inputBox}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
              }}
            >
              <Typography variant="h3" sx={styles.labelTypo}>
                Project Name
              </Typography>
              <Typography variant="subText" color="text.secondary">
                {taskName.length}/{MAX_PROJECT_NAME_LENGTH}
              </Typography>
            </Box>
            <InputField
              nonCircular
              value={taskName}
              name="taskName"
              onChange={(event) => setTaskName(event.target.value)}
              inputProps={{
                maxLength: MAX_PROJECT_NAME_LENGTH,
                'aria-label': 'Project Name',
              }}
              variant="outlined"
              sx={styles.inputField}
            />
          </Box>

          <StartAndEndPointPicker
            startTimeValues={startTimeValues}
            setStartTimeValues={setStartTimeValues}
            endTimeValues={endTimeValues}
            setEndTimeValues={setEndTimeValues}
          />

          <Box sx={styles.inputBox}>
            <Typography variant="h3" sx={styles.labelTypo}>
              Enter Marks
            </Typography>
            <Box sx={styles.marksBox}>
              <ButtonBase
                aria-label="Decrease marks"
                sx={styles.marksSymbol}
                onClick={decrementMarks}
              >
                &#8722;
              </ButtonBase>
              <InputBase
                value={marks}
                onChange={handleMarksChange}
                inputProps={{ inputMode: 'numeric', 'aria-label': 'Marks' }}
                sx={styles.marksValue}
              />
              <ButtonBase
                aria-label="Increase marks"
                sx={styles.marksSymbol}
                onClick={incrementMarks}
              >
                +
              </ButtonBase>
            </Box>
          </Box>

          <Box sx={styles.inputBox}>
            <Typography variant="h3" sx={styles.labelTypo}>
              Allow Late Submission
            </Typography>
            <RadioGroup
              row
              value={allowLateSubmission ? 'Yes' : 'No'}
              onChange={(event) =>
                setAllowLateSubmission(event.target.value === 'Yes')
              }
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </Box>

          <Box sx={styles.inputBox}>

            <Typography variant="h3" sx={styles.labelTypo}>
              Assign Groups
            </Typography>
            {/* <Typography variant="subText" color="secondary.main">
                  {groups.length === 0
                    ? 'Optional — add groups for group work, or assign to everyone individually'
                    : `${ungroupedStudents.length} of ${assignedStudents.length} students are not in a group and will not get this project`}
                </Typography> */}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: pxToRem(10), md: pxToRem(18), lg: pxTovW(20) },
              }}
            >
              <PrimaryButton
                onClick={() => {
                  setEditingGroup(null);
                  setStudentListPopupOpen(true);
                }}
                sx={{ width: { xs: '100%', md: pxToRem(220), lg: pxTovW(300) } }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: pxToRem(16), md: pxToRem(17), lg: pxTovW(18) },
                    color: '#FFFFFF',
                  }}
                >
                  Add Group
                </Typography>
              </PrimaryButton>

              <SecondaryButton
                variant="outlined"
                onClick={() => setPreviousGroupsPanelOpen((prev) => !prev)}
                disabled={selectedSectionIds.length === 0}
                styles={{
                  width: { xs: '100%', md: pxToRem(220), lg: pxTovW(300) },
                  height: { xs: pxToRem(52), lg: pxTovW(70) },
                  backgroundColor: 'common.white',
                  ml: { md: 'auto' },
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: pxToRem(16), md: pxToRem(17), lg: pxTovW(18) },
                    color: '#0AA34F'
                  }}
                >
                  Use Previous Groups
                </Typography>
              </SecondaryButton>
            </Box>

            <PreviousGroupsPanel
              open={previousGroupsPanelOpen}
              onClose={() => setPreviousGroupsPanelOpen(false)}
              // A project assigns to exactly one section.
              sectionId={selectedSectionIds[0]}
              existingGroupNames={groups.map((g) => g.groupName)}
              // A student belongs to only one group, so drop the ones already taken.
              unavailableStudentIds={getStudentIdsTakenByOtherGroups(groups)}
              onValidationError={(message) =>
                dispatch(
                  setToastInfo({ variant: 'error', label: message, open: true })
                )
              }
              onAddGroups={(picked) => {
                setGroups((current) => [
                  ...current,
                  ...picked.map((group) => ({
                    clientGroupId: createClientGroupId(),
                    groupName: group.groupName,
                    sectionId: group.sectionId,
                    studentIds: group.studentIds,
                  })),
                ]);
                setPreviousGroupsPanelOpen(false);
              }}
            />
          </Box>

          <Box>
            {groups.map((group) => (
              <Box key={group.clientGroupId} sx={styles.groupRow}>
                <Typography variant="h4" sx={{ flexGrow: 1 }}>
                  {group.groupName} ({group.studentIds.length})
                </Typography>

                <IconWrapper
                  name="edit"
                  parentFolder="icons"
                  type="png"
                  onClick={() => {
                    setEditingGroup(group);
                    setStudentListPopupOpen(true);
                  }}
                />
                <IconWrapper
                  name="deletehw"
                  parentFolder="icons"
                  type="png"
                  onClick={() => deleteGroup(group.clientGroupId)}
                />
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={styles.instructionBox}>
            <Box sx={styles.instructionCard}>
              <Box
                sx={{
                  height: '5px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <IconWrapper
                  name="edit"
                  parentFolder="icons"
                  type="png"
                  size="md"
                  customSx={{
                    height: { xs: pxToRem(34), md: pxToRem(27), lg: pxTovW(34) },
                    width: { xs: pxToRem(34), md: pxToRem(27), lg: pxTovW(34) },
                    cursor: 'pointer',
                  }}
                  onClick={() => setOpenInstructionsPopup(true)}
                />
              </Box>
              <Box sx={{ ...styles.textBox }}>
                <Typography variant="h2" fontWeight="bold">
                  Instructions :
                </Typography>
              </Box>
              {project_instructions?.map((elem: string, i: number) => (
                <Box key={i} sx={styles.textBox}>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <CheckOutlinedIcon fontSize="small" color="primary" />
                  </Box>
                  <Typography
                    variant={!isMobile && isIpad ? 'h3' : 'h4'}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {elem}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <HomeworkInstructionsPopup
          instructions={project_instructions}
          setInstructions={(arr) => dispatch(setProjectInstructions(arr))}
          open={openInstructionsPopup}
          handleClose={() => setOpenInstructionsPopup(false)}
        />
      </Box>

      <Box
        sx={{
          display: { xs: 'unset', md: 'none' },
          position: 'fixed',
          bottom: { xs: pxToRem(10) },
          width: { xs: '90vw' },
        }}
      >
        <PrimaryButton
          key={`assign_mobile_${minute}`}
          disabled={assignDisabled}
          onClick={assignClickHandler}
        >
          Assign
        </PrimaryButton>
      </Box>

      <AddGroupPopup
        open={studentListPopupOpen}
        handleClose={() => {
          setEditingGroup(null);
          setStudentListPopupOpen(false);
        }}
        sectionList={sectionList}
        setSectionList={setSectionList}
        classId={selected_class?.id}
        subjectId={selected_subject?.id}
        editingGroup={editingGroup}
        // A student can belong to only one group, so hide the ones already taken.
        unavailableStudentIds={getStudentIdsTakenByOtherGroups(
          groups,
          editingGroup?.clientGroupId
        )}
        existingGroupNames={groups
          .filter((g) => g.clientGroupId !== editingGroup?.clientGroupId)
          .map((g) => g.groupName)}
        onValidationError={(message) =>
          dispatch(
            setToastInfo({ variant: 'error', label: message, open: true })
          )
        }
        onSubmitAction={({
          clientGroupId,
          groupName,
          studentIds,
          sectionId,
        }) => {
          setGroups((current) =>
            clientGroupId
              ? current.map((group) =>
                group.clientGroupId === clientGroupId
                  ? { ...group, groupName, studentIds, sectionId }
                  : group
              )
              : [
                ...current,
                {
                  clientGroupId: createClientGroupId(),
                  groupName,
                  sectionId,
                  studentIds,
                },
              ]
          );
          setEditingGroup(null);
          setStudentListPopupOpen(false);
        }}
      />
    </Box>
  );
};
